// @ts-check

/** @file Durable storage for the app-level offline mutation queue. */

import LocalMutationLog from "velocious/build/src/sync/local-mutation-log.js"

import Sync from "../models/sync.js"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "./project-replica-contract.js"
import {syncUuid} from "./project-replica-mutation-log.js"

/** @import TenantHandle from "velocious/build/src/tenants/tenant-handle.js" */
/** @import {LocalMutationLogRecord, LocalMutationLogRecordsOptions} from "velocious/build/src/sync/local-mutation-log.js" */
/** @import ModelClassQuery from "velocious/build/src/database/query/model-class-query.js" */

/** Storage key distinguishing app-level offline queue rows from tenant intent rows. */
export const APP_OFFLINE_MUTATION_LOG_STORAGE_KEY = "awesome-tasks.app-offline-sync.queue"

/**
 * Row-oriented durable adapter for the app-level offline queue, bound to one
 * captured project replica handle.
 */
class AppOfflineMutationLogStorage {
  /**
   * Creates storage bound to one captured project handle.
   * @param {TenantHandle} handle Captured project handle.
   */
  constructor(handle) {
    this.handle = handle
    /** @type {Map<string, Map<string, LocalMutationLogRecord>>} */
    this.recordsByStorageKey = new Map()
    /** @type {Promise<void>} */
    this.pendingWrites = Promise.resolve()
    /** @type {Promise<unknown>} */
    this.observedWriteFailure = Promise.resolve(null)
  }

  /** Loads durable app-queue rows before the provider can append to them. */
  async initialize() {
    await this.withSyncs(async (syncs) => {
      for (const row of await syncs.toArray()) {
        const storageKey = row.command()
        if (!storageKey?.startsWith(APP_OFFLINE_MUTATION_LOG_STORAGE_KEY)) continue
        const record = rowRecord(row)
        if (record) this.storageRecords(storageKey).set(record.id, record)
      }
    })
  }

  /**
   * Adds a durable app-queue intent to the owned partition.
   * @param {string} storageKey Partition key.
   * @param {LocalMutationLogRecord} record Durable record.
   */
  async appendRecord(storageKey, record) {
    this.storageRecords(storageKey).set(record.id, cloneRecord(record))
    this.enqueueWrite(async (syncs) => { await syncs.create(syncAttributes(storageKey, record)) })
  }

  /**
   * Removes resolved app-queue intents from the owned partition.
   * @param {string} storageKey Partition key.
   * @param {string[]} ids Record ids.
   */
  async deleteRecords(storageKey, ids) {
    const records = this.storageRecords(storageKey)
    for (const id of ids) records.delete(id)
    this.enqueueWrite(async (syncs) => {
      for (const id of ids) {
        const row = await syncs.findBy({command: storageKey, id})
        if (row) await row.destroy()
      }
    })
  }

  /**
   * Finds the next monotonically increasing local sequence.
   * @param {string} storageKey Partition key.
   * @returns {Promise<number>} Value greater than every stored sequence.
   */
  async nextSequence(storageKey) {
    const greatest = [...this.storageRecords(storageKey).values()]
      .reduce((value, record) => Math.max(value, record.sequence), 0)
    return greatest + 1
  }

  /**
   * Reads one app-queue intent by id, or null when it is absent.
   * @param {string} storageKey Partition key.
   * @param {string} id Durable mutation identity.
   * @returns {Promise<LocalMutationLogRecord | null>} Isolated record copy.
   */
  async record(storageKey, id) {
    const record = this.storageRecords(storageKey).get(id)
    return record ? cloneRecord(record) : null
  }

  /**
   * Reads ordered isolated copies of matching app-queue intents.
   * @param {string} storageKey Partition key.
   * @param {LocalMutationLogRecordsOptions} [options] Query options.
   * @returns {Promise<LocalMutationLogRecord[]>} Stored records ordered by sequence.
   */
  async records(storageKey, options = {}) {
    const statuses = options.statuses ? new Set(options.statuses) : null
    return [...this.storageRecords(storageKey).values()]
      .filter((record) => !statuses || statuses.has(record.status))
      .sort((left, right) => left.sequence - right.sequence)
      .map(cloneRecord)
  }

  /**
   * Replaces one existing app-queue intent.
   * @param {string} storageKey Partition key.
   * @param {LocalMutationLogRecord} record Replacement record.
   * @throws {Error} When the durable intent has disappeared.
   * @rejects {Error} When the durable intent has disappeared.
   */
  async updateRecord(storageKey, record) {
    if (!this.storageRecords(storageKey).has(record.id)) {
      throw new Error(`App offline mutation log record disappeared: ${record.id}`)
    }
    this.storageRecords(storageKey).set(record.id, cloneRecord(record))
    this.enqueueWrite(async (syncs) => {
      const row = await syncs.findBy({command: storageKey, id: record.id})
      if (!row) throw new Error(`App offline mutation log record disappeared: ${record.id}`)
      row.assign(syncAttributes(storageKey, record))
      await row.save()
    })
  }

  /**
   * Awaits app-queue acknowledgement writes after the replica releases its
   * database operation.
   * @returns {Promise<void>} Durable write completion.
   * @rejects {Error} When a queued row write or database flush fails.
   */
  async flushPendingWrites() {
    const failure = await this.observedWriteFailure
    if (failure) throw failure
    await this.handle.flush({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
  }

  /**
   * Returns the mutable cache owned by this storage adapter.
   * @param {string} storageKey Partition key.
   * @returns {Map<string, LocalMutationLogRecord>} Mutable owned partition.
   */
  storageRecords(storageKey) {
    let records = this.recordsByStorageKey.get(storageKey)
    if (!records) {
      records = new Map()
      this.recordsByStorageKey.set(storageKey, records)
    }
    return records
  }

  /**
   * Serializes row writes for this app-queue partition.
   * @param {(syncs: ModelClassQuery<typeof Sync>) => Promise<void>} callback Row mutation callback.
   */
  enqueueWrite(callback) {
    const write = this.pendingWrites.then(async () => await this.withSyncs(callback))
    this.pendingWrites = write
    this.observedWriteFailure = write.then(() => null, (error) => error)
  }

  /**
   * Runs one operation against this replica's Sync model.
   * @template Result The callback's operation result type.
   * @param {(syncs: ModelClassQuery<typeof Sync>) => Promise<Result>} callback Operation callback.
   * @returns {Promise<Result>} Callback result.
   */
  async withSyncs(callback) {
    return await this.handle.databaseOperation({
      databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
      name: "App offline mutation log"
    }, async (operation) => await callback(operation.forModel(Sync)))
  }
}

/**
 * Creates the app-level offline mutation log bound to one project replica.
 * @param {{handle: TenantHandle, idGenerator?: () => string, now?: () => Date}} args Adapter inputs.
 * @returns {Promise<{flushPendingWrites: () => Promise<void>, mutationLog: LocalMutationLog}>} Initialized log and durability drain.
 */
export async function createAppOfflineMutationLog({handle, idGenerator = syncUuid, now = () => new Date()}) {
  const storage = new AppOfflineMutationLogStorage(handle)
  await storage.initialize()
  return {
    flushPendingWrites: async () => await storage.flushPendingWrites(),
    mutationLog: new LocalMutationLog({
      idGenerator,
      now,
      storage,
      storageKey: APP_OFFLINE_MUTATION_LOG_STORAGE_KEY
    })
  }
}

/**
 * Maps one framework offline-queue record to a local Sync row, deriving the
 * required `resource_id` from the mutation's primary-key attribute when the
 * record carries no explicit `payload.resourceId`.
 * @param {string} storageKey Partition key.
 * @param {LocalMutationLogRecord} record Log record.
 * @returns {Record<string, unknown>} Local Sync attributes.
 */
function syncAttributes(storageKey, record) {
  const resourceId = derivedResourceId(record)
  return {
    command: storageKey,
    data: JSON.parse(JSON.stringify(record)),
    failureDetails: null,
    failureReason: record.status === "conflict" || record.status === "rejected" ? record.status : null,
    id: record.id,
    predecessorMutationId: record.dependencies.at(-1)?.clientMutationId ?? null,
    resourceId,
    resourceType: record.mutation.model,
    sequence: record.sequence,
    // App-queue rows map the framework `synced` terminal state to the
    // replica's `success` state so the existing pending-mutation count (which
    // treats any non-`intent-` state as unsynced unless it is `success`)
    // excludes resolved offline work; `pending`/`conflict`/`rejected` count.
    state: record.status === "synced" ? "success" : record.status,
    syncType: String(record.mutation.payload?.syncType || record.mutation.operation)
  }
}

/**
 * Resolves the durable row's required `resource_id`. The tenant path always
 * supplies `payload.resourceId`; the framework's app-level offline queue does
 * not, so the primary-key attribute is used as a stable stand-in (offline
 * creates generate a UUID primary key; updates/destroys carry the row id).
 * @param {LocalMutationLogRecord} record Log record.
 * @returns {string} Stable resource id for the Sync row.
 */
function derivedResourceId(record) {
  const payloadId = record.mutation.payload?.resourceId
  if (typeof payloadId === "string" && payloadId.length > 0) return payloadId

  const attributes = record.mutation.attributes || {}
  for (const key of ["id", "taskId", "boardId"]) {
    const value = attributes[key]
    if (typeof value === "string" && value.length > 0) return value
  }

  // Fall back to the client mutation id; it is unique per queued mutation and
  // stable across append/update, so the row identity stays deterministic.
  return record.mutation.clientMutationId
}

/**
 * Restores one framework record from a local Sync row.
 * @param {InstanceType<typeof Sync> | null | undefined} row Stored row.
 * @returns {LocalMutationLogRecord | null} Restored record.
 * @throws {Error} When the row payload is malformed.
 */
function rowRecord(row) {
  if (!row) return null
  const rawData = row.data()
  const data = typeof rawData === "string" ? JSON.parse(rawData) : rawData
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error(`App offline mutation log row ${row.id()} has invalid data`)
  }
  return /** @type {LocalMutationLogRecord} */ (JSON.parse(JSON.stringify(data)))
}

/**
 * Clones a framework record at the storage boundary.
 * @param {LocalMutationLogRecord} record Source record.
 * @returns {LocalMutationLogRecord} JSON-safe clone.
 */
function cloneRecord(record) {
  return /** @type {LocalMutationLogRecord} */ (JSON.parse(JSON.stringify(record)))
}

/** Active app-queue logs keyed by canonical project identity. */
const activeAppOfflineLogs = new Map()

/**
 * Registers an opened replica's app-level offline queue log.
 * @param {{identity: {projectId: string, userId: string}, flushPendingWrites: () => Promise<void>, mutationLog: LocalMutationLog}} args Open replica binding.
 * @returns {void} Nothing.
 */
export function registerAppOfflineMutationLog({identity, flushPendingWrites, mutationLog}) {
  activeAppOfflineLogs.set(`${identity.userId}:${identity.projectId}`, {flushPendingWrites, identity, mutationLog})
}

/**
 * Removes one replica's app-level offline queue log.
 * @param {{identity: {projectId: string, userId: string}, mutationLog: LocalMutationLog}} args Exact binding.
 * @returns {void} Nothing.
 */
export function unregisterAppOfflineMutationLog({identity, mutationLog}) {
  const key = `${identity.userId}:${identity.projectId}`
  const registered = activeAppOfflineLogs.get(key)
  if (registered?.mutationLog === mutationLog) activeAppOfflineLogs.delete(key)
}
